import { TypedDocumentNode, gql } from '@apollo/client';
import { ComponentProps, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useResultsStoryAssets } from '@sorare/core/src/components/animations/AnimatedStory/useResultsStoryAssets';
import { ANY_SPORT_MY_CARDS_BOXES } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { isTypeFn } from '@sorare/core/src/lib/gql';

import { FixtureOverview } from 'components/fixture/FixtureTeamsOverview/FixtureOverview';
import { LastFixtureStoryStep } from 'components/so5/LastFixtureStory/LastFixtureStoryStep';
import { StoryState } from 'components/so5/LastFixtureStory/types';
import { useClaimRewards } from 'hooks/so5/useClaimRewards';

import { Title } from '../LastFixtureStoryStep/Title';
import { VerificationBanner } from '../VerificationBanner';
import { DeliverableItemBlock } from './DeliverableItemBlock';
import {
  RewardsRecap_so5Fixture,
  RewardsRecap_so5Reward,
} from './__generated__/index.graphql';
import type {
  UnclaimedDeliverableItemOrders,
  UnclaimedDeliverableItemOrdersVariables,
} from './__generated__/index.graphql';
import { useRewardsVerification } from './useRewardsVerification';

const FullWidthVertical = styled(Vertical).attrs({ gap: 3 })`
  width: 100%;
`;

type CtaProps = {
  box: number;
  card: number;
  onNext?: () => void;
  onClose?: () => void;
};
const Cta = ({ box, card, onNext, onClose }: CtaProps) => {
  const { generateSportContextPath } = useSportContext();
  const navigate = useNavigate();

  if (card > 0 && onNext) {
    return (
      <Button size="medium" fullWidth color="secondary" onClick={onNext}>
        <FormattedMessage
          id="RewardsRecap.cta.revealMyCard"
          defaultMessage="Reveal {card, plural, one {card} other {cards}}"
          values={{ card }}
        />
      </Button>
    );
  }
  if (box > 0 && !onNext) {
    return (
      <Button
        size="medium"
        fullWidth
        color="secondary"
        onClick={async () => {
          await onClose?.();
          navigate(generateSportContextPath(ANY_SPORT_MY_CARDS_BOXES));
        }}
      >
        <FormattedMessage
          id="CardRewardsReveal.cta.goToMyBoxes"
          defaultMessage="Go to My Boxes"
        />
      </Button>
    );
  }
  return null;
};

const UNCLAIMED_DELIVERABLE_ITEM_ORDERS = gql`
  query UnclaimedDeliverableItemOrders($sport: Sport) {
    currentUser {
      slug
      unclaimedDeliverableItemOrders(sport: $sport) {
        id
        ...DeliverableItemBlock_deliverableItemOrder
      }
    }
  }
  ${DeliverableItemBlock.fragments.deliverableItemOrder}
` as TypedDocumentNode<
  UnclaimedDeliverableItemOrders,
  UnclaimedDeliverableItemOrdersVariables
>;

type Props = {
  so5Fixture?: RewardsRecap_so5Fixture;
  so5Rewards: RewardsRecap_so5Reward[];
  onNext?: () => void;
  onClose: ComponentProps<typeof LastFixtureStoryStep>['onClose'];
  from?: StoryState;
};
export const RewardsRecap = ({
  from: fromProp,
  so5Fixture,
  so5Rewards,
  onNext,
  onClose,
}: Props) => {
  const { sport } = useSportContext();
  const from = useRef(fromProp);
  const { claimableRewardsByType, rewardsByType, verificationNeeded } =
    useRewardsVerification(so5Rewards);

  const [claimed, setClaimed] = useState(false);
  const [claimRewards] = useClaimRewards();

  const { intro, tunnel } = useResultsStoryAssets();

  const { data } = useQuery(UNCLAIMED_DELIVERABLE_ITEM_ORDERS, {
    variables: { sport },
    skip: !claimed,
  });

  const deliverableItemOrders =
    data?.currentUser?.unclaimedDeliverableItemOrders || [];

  const { total, card, ...cardExcludedClaimableRewards } =
    claimableRewardsByType;
  const cardExcludedClaimableRewardIds = Object.values(
    cardExcludedClaimableRewards
  ).flatMap(reward => reward.map(({ id }) => id));
  if (!claimed && cardExcludedClaimableRewardIds.length) {
    claimRewards(cardExcludedClaimableRewardIds);
    setClaimed(true);
  }

  return (
    <LastFixtureStoryStep
      title={<Title type="rewardsRecap" so5Fixture={so5Fixture} />}
      video={{
        asset: from.current === 'intro' ? intro : tunnel,
        color: 'var(--c-neutral-600)',
      }}
      onNext={onNext}
      onClose={onClose}
      cta={
        rewardsByType.card.length > 0 || rewardsByType.box.length > 0 ? (
          <Cta
            card={rewardsByType.card.length}
            box={rewardsByType.box.length}
            onNext={onNext}
            onClose={onClose}
          />
        ) : null
      }
      banner={<VerificationBanner verificationNeeded={verificationNeeded} />}
    >
      <FullWidthVertical>
        <FixtureOverview
          so5Fixture={so5Fixture}
          so5Rewards={so5Rewards}
          onlyRewards
        />
        <DeliverableItemBlock
          deliverableItemOrders={deliverableItemOrders}
          deliverableItems={rewardsByType.deliverableItem
            .flatMap(so5Reward => so5Reward.rewards)
            .filter(isTypeFn('DeliverableItemReward'))}
        />
      </FullWidthVertical>
    </LastFixtureStoryStep>
  );
};

RewardsRecap.fragments = {
  so5Reward: gql`
    fragment RewardsRecap_so5Reward on So5Reward {
      slug
      id
      rewards {
        id
        ...DeliverableItemBlock_deliverableItemReward
      }
      ...FixtureOverview_so5RewardOrConfig
      ...useRewardsVerification_so5Reward
    }
    ${FixtureOverview.fragments.so5RewardOrConfig}
    ${useRewardsVerification.fragments.so5Reward}
    ${DeliverableItemBlock.fragments.deliverableItemReward}
  ` as TypedDocumentNode<RewardsRecap_so5Reward>,
  so5Fixture: gql`
    fragment RewardsRecap_so5Fixture on So5Fixture {
      slug
      gameWeek
      ...FixtureOverview_so5Fixture
      ...Title_so5Fixture
    }
    ${FixtureOverview.fragments.so5Fixture}
    ${Title.fragments.so5Fixture}
  ` as TypedDocumentNode<RewardsRecap_so5Fixture>,
};
